/*
 * Copyright 2021 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __assign, __rest } from "tslib";
import classNames from "classnames";
import * as React from "react";
import { Classes as CoreClasses, Utils as CoreUtils, mergeRefs, } from "@blueprintjs/core";
import * as Classes from "./classes";
import { Popover2 } from "./popover2";
export var ContextMenu2 = React.forwardRef(function (props, userRef) {
    var _a;
    var _b;
    var className = props.className, children = props.children, content = props.content, _c = props.disabled, disabled = _c === void 0 ? false : _c, onContextMenu = props.onContextMenu, popoverProps = props.popoverProps, _d = props.tagName, tagName = _d === void 0 ? "div" : _d, restProps = __rest(props, ["className", "children", "content", "disabled", "onContextMenu", "popoverProps", "tagName"]);
    var _e = React.useState(undefined), targetOffset = _e[0], setTargetOffset = _e[1];
    var _f = React.useState(), mouseEvent = _f[0], setMouseEvent = _f[1];
    var _g = React.useState(false), isOpen = _g[0], setIsOpen = _g[1];
    var containerRef = React.useRef(null);
    // If disabled prop is changed, we don't want our old context menu to stick around.
    // If it has just been enabled (disabled = false), then the menu ought to be opened by
    // a new mouse event. Users should not be updating this prop in the onContextMenu callback
    // for this component (that will lead to unpredictable behavior).
    React.useEffect(function () {
        setIsOpen(false);
    }, [disabled]);
    var cancelContextMenu = React.useCallback(function (e) { return e.preventDefault(); }, []);
    var handlePopoverInteraction = React.useCallback(function (nextOpenState) {
        if (!nextOpenState) {
            setIsOpen(false);
            setMouseEvent(undefined);
        }
    }, []);
    var targetRef = React.useRef(null);
    var renderTarget = React.useCallback(function (_a) {
        var ref = _a.ref;
        return (React.createElement("div", { className: Classes.CONTEXT_MENU2_POPOVER2_TARGET, style: targetOffset, ref: mergeRefs(ref, targetRef) }));
    }, [targetOffset]);
    var isDarkTheme = React.useMemo(function () { return CoreUtils.isDarkTheme(targetRef.current); }, [targetRef.current]);
    var contentProps = { isOpen: isOpen, mouseEvent: mouseEvent, targetOffset: targetOffset };
    // only render the popover if there is content in the context menu;
    // this avoid doing unnecessary rendering & computation
    var menu = disabled ? undefined : CoreUtils.isFunction(content) ? content(contentProps) : content;
    var maybePopover = menu === undefined ? undefined : (React.createElement(Popover2, __assign({}, popoverProps, { content: 
        // this prevents right-clicking inside our context menu
        React.createElement("div", { onContextMenu: cancelContextMenu }, menu), enforceFocus: false, 
        // Generate key based on offset so that a new Popover instance is created
        // when offset changes, to force recomputing position.
        key: getPopoverKey(targetOffset), hasBackdrop: true, isOpen: isOpen, minimal: true, onInteraction: handlePopoverInteraction, popoverClassName: classNames(popoverProps === null || popoverProps === void 0 ? void 0 : popoverProps.popoverClassName, (_a = {}, _a[CoreClasses.DARK] = isDarkTheme, _a)), placement: "right-start", positioningStrategy: "fixed", rootBoundary: "viewport", renderTarget: renderTarget, transitionDuration: (_b = popoverProps === null || popoverProps === void 0 ? void 0 : popoverProps.transitionDuration) !== null && _b !== void 0 ? _b : 100 })));
    var handleContextMenu = React.useCallback(function (e) {
        // support nested menus (inner menu target would have called preventDefault())
        if (e.defaultPrevented) {
            return;
        }
        // If disabled, we should avoid this extra work. Otherwise: if using the child function API,
        // we need to make sure contentProps is up to date for correctness, so we handle the event regardless
        // of whether the consumer returned an undefined menu.
        var shouldHandleEvent = !disabled && (CoreUtils.isFunction(children) || maybePopover !== undefined);
        if (shouldHandleEvent) {
            e.preventDefault();
            e.persist();
            setMouseEvent(e);
            var _a = getContainingBlockOffset(containerRef.current), left = _a.left, top_1 = _a.top;
            setTargetOffset({ left: e.clientX - left, top: e.clientY - top_1 });
            setIsOpen(true);
        }
        onContextMenu === null || onContextMenu === void 0 ? void 0 : onContextMenu(e);
    }, [containerRef.current, onContextMenu, disabled]);
    var containerClassName = classNames(className, Classes.CONTEXT_MENU2);
    if (CoreUtils.isFunction(children)) {
        return children({
            className: containerClassName,
            contentProps: contentProps,
            onContextMenu: handleContextMenu,
            popover: maybePopover,
            ref: containerRef,
        });
    }
    else {
        return React.createElement(tagName, __assign({ className: containerClassName, onContextMenu: handleContextMenu, ref: mergeRefs(containerRef, userRef) }, restProps), maybePopover, children);
    }
});
ContextMenu2.displayName = "Blueprint.ContextMenu2";
function getContainingBlockOffset(targetElement) {
    if (targetElement != null) {
        var containingBlock = targetElement.closest("." + CoreClasses.FIXED_POSITIONING_CONTAINING_BLOCK);
        if (containingBlock != null) {
            return containingBlock.getBoundingClientRect();
        }
    }
    return { left: 0, top: 0 };
}
function getPopoverKey(targetOffset) {
    return targetOffset === undefined ? "default" : targetOffset.left + "x" + targetOffset.top;
}
//# sourceMappingURL=contextMenu2.js.map